import React, { useEffect, useMemo, useState } from 'react';

import { FormControl, FormHelperText, Grid, InputLabel } from '@material-ui/core';
import { Field } from 'formik';
import { useSelector } from 'react-redux';
import { SERVICES_CODES } from 'app/config/services';
import { RadioButtonGroup } from 'app/components/FormElements';
import HighlightableTextField from '../HighlightableTextField';
import Skeleton from '../Skeleton';
import HighlightableRadio from '../HighlightableRadio';
import HighlightableSelect from '../HighlightableSelect';
import { labelObrigatoriedade } from 'app/utils/helpers';
import companyEndpoints from '../../../api/services/companyEndpoints';

function AdditionalData(props) {
    const {
        classes,
        loadingDetran,
        foundOnDetran,
        blockChangesField,
        handleBlurField,
        values,
        errors,
        touched,
        setFieldValue,
        campoDiferenteDoOriginal,
        highlightEnabled,
        isFieldDisabled,
        requiredFields,
    } = props;

    const service = useSelector((state) => state.services.details);
    const accessibilityType = useSelector((state) => state.vehicle.accessibilityType);
    const vehicleClassification = useSelector((state) => state.vehicle.vehicleClassification);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const request = useSelector((state) => state.requests.details);

    const [subSystems, setSubSystems] = useState([]);
    useEffect(() => {
        (async () => {
            const cdEmpresa =
                activeCompany?.cdEmpresa ?? request?.cdEmpresa ?? internalNewRequest?.company?.cdEmpresa ?? -1;
            const subsistemasRequest = await companyEndpoints.subsistemasDisponiveisPara(cdEmpresa);
            setSubSystems(subsistemasRequest.data);
        })();
        // eslint-disable-next-line
    }, [activeCompany, request]);

    const [qtCapacidade, setQtCapacidade] = useState('');
    const [deUtilizacao, setDeUtilizacao] = useState('');
    const [subSystemsSelect, setSubSystemsSelect] = useState([]);

    const validateAcessibilidade = (value) => {
        if (!isTipoAcessibilidadeDisabled && qtCapacidade >= 22 && deUtilizacao === 'Sistema' && value === '') {
            return 'Campo obrigatório';
        }
        return '';
    };

    const labelObrigatoriedadeAcessibilidade = useMemo(() => {
        return qtCapacidade >= 22 && deUtilizacao === 'Sistema' ? '*' : '';
    }, [qtCapacidade, deUtilizacao]);

    useEffect(() => {
        if (values.qtCapacidade) {
            setQtCapacidade(values.qtCapacidade);
        } else {
            setQtCapacidade('');
        }
    }, [values]);

    const handleChangeUtilizacao = (value, _subsystems, formChanged = false) => {
        if (!value) return;
        _subsystems = _subsystems ?? subSystems; // para se comportar corretamente no carregamento dinâmico dos subsistemas.
        let temp;
        if (value && value !== '') {
            temp = _subsystems.filter((s) => {
                if (!s.deUtilizacao) return false;
                return value.toUpperCase() === s.deUtilizacao.toUpperCase();
            });
        }
        setSubSystemsSelect(temp);
        setDeUtilizacao(value);
        if (formChanged) setFieldValue('cdSubSistema', '');
    };

    const handleChangeCapacity = (e, setFieldValue) => {
        const name = e.target.name;
        const value = e.target.value;
        setQtCapacidade(value);
        setFieldValue(name, value);
    };

    const resultDetran = (elem) => {
        return foundOnDetran.includes(elem);
    };

    const isTipoAcessibilidadeDisabled = useMemo(
        () => {
            return (
                isFieldDisabled('cdTipoAcessibilidade') ||
                (resultDetran('cdTipoAcessibilidade') && values.cdTipoAcessibilidade !== '')
            );
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(() => {
        (() => {
            handleChangeUtilizacao(values.deUtilizacao, subSystems);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.deUtilizacao, subSystems]);

    return (
        <>
            <Grid item xs={12}>
                <h2 className={classes.title}>Informações complementares</h2>
            </Grid>
            <Grid item xs={12}>
                <h3 className={classes.subtitle}>Capacidade</h3>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <Field
                        name="qtCapacidade"
                        label={`Capacidade${labelObrigatoriedade('qtCapacidade', requiredFields)}`}
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('qtCapacidade') ||
                            (resultDetran('qtCapacidade') && values.qtCapacidade !== '') ||
                            blockChangesField ||
                            loadingDetran
                        }
                        error={!!errors.qtCapacidade}
                        helperText={errors.qtCapacidade ? errors.qtCapacidade : ''}
                        fullWidth
                        value={qtCapacidade}
                        onChange={(e) => handleChangeCapacity(e, setFieldValue)}
                        onBlur={(e) => handleBlurField(e, setFieldValue)}
                        as={HighlightableTextField}
                        safeToHighlight={highlightEnabled}
                        isHighlightedFunction={campoDiferenteDoOriginal('qtCapacidade')}
                        inputProps={{
                            maxLength: 3,
                        }}
                    />
                </Skeleton>
            </Grid>
            <Grid item xs={12}>
                <h3
                    className={
                        classes.subtitle
                    }>{`Utilização${labelObrigatoriedade('deUtilizacao', requiredFields)}`}</h3>
            </Grid>
            <Grid item xs={12} sm={6} container className={classes.verticalMiddle}>
                <RadioButtonGroup
                    id="deUtilizacao"
                    label=""
                    value={values.deUtilizacao}
                    error={errors.deUtilizacao}
                    touched={touched.deUtilizacao}
                    style={{ width: '100%', display: 'flex' }}>
                    <Grid item sm={6}>
                        <Field
                            component={HighlightableRadio}
                            safeToHighlight={highlightEnabled}
                            isHighlightedFunction={(value) =>
                                campoDiferenteDoOriginal('deUtilizacao')(value) && value === values?.deUtilizacao
                            }
                            name="deUtilizacao"
                            id="Serviços especiais"
                            label="Serviços especiais"
                            disabled={isFieldDisabled('deUtilizacao')}
                            onClick={(e) => handleChangeUtilizacao(e.target.value, null, true)}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        <Field
                            component={HighlightableRadio}
                            safeToHighlight={highlightEnabled}
                            isHighlightedFunction={(value) =>
                                campoDiferenteDoOriginal('deUtilizacao')(value) && value === values?.deUtilizacao
                            }
                            name="deUtilizacao"
                            id="Sistema"
                            label="Sistema"
                            disabled={
                                isFieldDisabled('deUtilizacao') ||
                                (resultDetran('deUtilizacao') && values.deUtilizacao !== '') ||
                                service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS_SEM_CRC ||
                                service.cdServico === SERVICES_CODES.ALTERACAO_VEICULOS_SEM_CRC
                            }
                            onClick={(e) => handleChangeUtilizacao(e.target.value, null, true)}
                        />
                    </Grid>
                </RadioButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControl
                    variant="filled"
                    margin="dense"
                    disabled={
                        isFieldDisabled('cdSubSistema') || (resultDetran('cdSubSistema') && values.cdSubSistema !== '')
                    }
                    fullWidth
                    error={!!errors.cdSubSistema}>
                    <InputLabel
                        htmlFor="cdSubSistema"
                        shrink={
                            values?.cdSubSistema?.toString()?.length > 0
                        }>{`Subsistema${labelObrigatoriedade('cdSubSistema', requiredFields)}`}</InputLabel>
                    <Field
                        name="cdSubSistema"
                        id="cdSubSistema"
                        native
                        fullWidth
                        as={HighlightableSelect}
                        isHighlightedFunction={campoDiferenteDoOriginal('cdSubSistema')}
                        safeToHighlight={highlightEnabled}>
                        <option aria-label="None" value="" />
                        {subSystemsSelect?.map((elem, index) => {
                            return (
                                <option key={`sub-${index}`} value={elem.cdSubSistema}>
                                    {elem.deSubSistema}
                                </option>
                            );
                        })}
                    </Field>
                    {errors.cdSubSistema ? <FormHelperText>{errors.cdSubSistema}</FormHelperText> : null}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormControl
                    variant="filled"
                    margin="dense"
                    disabled={
                        isFieldDisabled('cdTipoVeiculo') ||
                        (resultDetran('cdTipoVeiculo') && values.cdTipoVeiculo !== '')
                    }
                    fullWidth
                    error={!!errors.cdTipoVeiculo}>
                    <InputLabel htmlFor="cdTipoVeiculo">{`Tipo de veículo${labelObrigatoriedade('cdTipoVeiculo', requiredFields)}`}</InputLabel>
                    <Field
                        name="cdTipoVeiculo"
                        id="cdTipoVeiculo"
                        native
                        fullWidth
                        as={HighlightableSelect}
                        isHighlightedFunction={campoDiferenteDoOriginal('cdTipoVeiculo')}
                        safeToHighlight={highlightEnabled}>
                        <option aria-label="None" value="" />
                        {vehicleType.map((elem, index) => {
                            return (
                                <option key={`type-${index}`} value={elem.cdTipoVeiculo}>
                                    {elem.deTipoVeiculo}
                                </option>
                            );
                        })}
                    </Field>
                    {errors.cdTipoVeiculo ? <FormHelperText>{errors.cdTipoVeiculo}</FormHelperText> : null}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormControl
                    variant="filled"
                    margin="dense"
                    disabled={isTipoAcessibilidadeDisabled}
                    fullWidth
                    error={!!errors.cdTipoAcessibilidade}>
                    <InputLabel htmlFor="cdTipoAcessibilidade">{`Tipo de Acessibilidade${labelObrigatoriedadeAcessibilidade}`}</InputLabel>
                    <Field
                        name="cdTipoAcessibilidade"
                        id="cdTipoAcessibilidade"
                        native
                        fullWidth
                        as={HighlightableSelect}
                        isHighlightedFunction={campoDiferenteDoOriginal('cdTipoAcessibilidade')}
                        safeToHighlight={highlightEnabled}
                        validate={validateAcessibilidade}>
                        <option aria-label="None" value="" />
                        {accessibilityType.map((elem, index) => {
                            return (
                                <option key={`access-${index}`} value={elem.cdTipoAcessibilidade}>
                                    Letra {elem.sgTipoAcessibilidade}
                                </option>
                            );
                        })}
                    </Field>
                    {errors.cdTipoAcessibilidade ? (
                        <FormHelperText>{errors.cdTipoAcessibilidade}</FormHelperText>
                    ) : null}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormControl
                    variant="filled"
                    margin="dense"
                    disabled={
                        isFieldDisabled('cdClassificacaoVeiculo') ||
                        (resultDetran('cdClassificacaoVeiculo') && values.cdClassificacaoVeiculo !== '') ||
                        blockChangesField
                    }
                    fullWidth
                    error={!!errors.cdClassificacaoVeiculo}>
                    <InputLabel htmlFor="cdClassificacaoVeiculo">{`Classificação${labelObrigatoriedade('cdClassificacaoVeiculo', requiredFields)}`}</InputLabel>
                    <Field
                        name="cdClassificacaoVeiculo"
                        id="cdClassificacaoVeiculo"
                        native
                        fullWidth
                        as={HighlightableSelect}
                        isHighlightedFunction={campoDiferenteDoOriginal('cdClassificacaoVeiculo')}
                        safeToHighlight={highlightEnabled}>
                        <option aria-label="None" value="" />
                        {vehicleClassification.map((elem, index) => {
                            return (
                                <option key={`class-${index}`} value={elem.cdClassificacaoVeiculo}>
                                    {elem.deClassificacaoVeiculo}
                                </option>
                            );
                        })}
                    </Field>
                    {errors.cdClassificacaoVeiculo ? (
                        <FormHelperText>{errors.cdClassificacaoVeiculo}</FormHelperText>
                    ) : null}
                </FormControl>
            </Grid>
        </>
    );
}

export default AdditionalData;
